import React, { useState, useRef, useEffect } from "react";
import { auth } from "../firebase";
import { sendEmailVerification } from "firebase/auth";
import {
  makeRecaptcha, startEnroll, finishEnroll,
  toE164, looksLikeMobile, mfaErrorMessage,
} from "../utils/mfa";

// 2段階認証の登録画面。
// 給与や従業員情報を扱う管理者を守るため、SMSで本人確認を追加する。
// Firebaseの仕様でメール確認済みでないと登録できないので、
// 未確認の場合はその案内から始める。

function MfaSetup({ user, canSkip, graceUntil, onSkip, onDone }) {
  // email → phone → code の順に進む
  const [step, setStep]   = useState(user.emailVerified ? "phone" : "email");
  const [phone, setPhone] = useState("");
  const [code, setCode]   = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo]   = useState("");

  const verifierRef = useRef(null);

  // 画面を離れるときにreCAPTCHAを片付ける。残すと再表示で例外になる。
  useEffect(() => () => { try { verifierRef.current?.clear(); } catch { /* 片付けの失敗は無視 */ } }, []);

  const freshVerifier = () => {
    try { verifierRef.current?.clear(); } catch { /* 片付けの失敗は無視 */ }
    verifierRef.current = null;
    // clear() だけでは要素の中身が残り、作り直すと
    // 「すでにレンダリングされています」で失敗するため、自分で空にする
    const el = document.getElementById("mfa-recaptcha");
    if (el) el.innerHTML = "";
    verifierRef.current = makeRecaptcha("mfa-recaptcha");
    return verifierRef.current;
  };

  // ---------- メール確認 ----------
  const sendVerifyMail = async () => {
    setBusy(true); setError(""); setInfo("");
    try {
      await sendEmailVerification(user);
      setInfo(`${user.email} に確認メールを送りました。メール内のリンクを開いてから、下の「確認しました」を押してください。`);
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const recheckEmail = async () => {
    setBusy(true); setError(""); setInfo("");
    try {
      // 確認の反映が遅れることがあるので、トークンを取り直してから読み直す
      try { await auth.currentUser?.getIdToken(true); } catch { /* 取り直せなくても続行 */ }
      await user.reload();
      if (auth.currentUser?.emailVerified) {
        setStep("phone");
      } else {
        setError("まだ確認できていません。メール内のリンクを開いてから、もう一度押してください。「確認メールを送る」を押し直す必要はありません。それでも進まないときは、いったんログアウトして入り直してください。");
      }
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // ---------- 電話番号 ----------
  const sendCode = async () => {
    if (!looksLikeMobile(phone)) {
      setError("携帯電話の番号を入力してください（例：090-1234-5678）");
      return;
    }
    setBusy(true); setError(""); setInfo("");
    try {
      const id = await startEnroll(user, toE164(phone), freshVerifier());
      setVerificationId(id);
      setStep("code");
      setInfo("確認コードを送りました。届いた6桁の数字を入力してください。");
    } catch (err) {
      console.error("確認コードの送信に失敗しました:", err);
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // ---------- 確認コード ----------
  const submitCode = async () => {
    if (code.replace(/\D/g, "").length !== 6) {
      setError("6桁の確認コードを入力してください");
      return;
    }
    setBusy(true); setError("");
    try {
      await finishEnroll(user, verificationId, code.replace(/\D/g, ""));
      setStep("done");
    } catch (err) {
      console.error("2段階認証の登録に失敗しました:", err);
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const graceText = graceUntil
    ? `${graceUntil.toLocaleDateString("ja-JP")}まで、あとで設定することもできます。`
    : "";

  return (
    <div style={st.wrap}>
      <div style={st.card}>
        <div style={st.icon}>🔐</div>
        <h2 style={st.title}>2段階認証の設定</h2>
        <p style={st.lead}>
          給与や従業員の情報を扱うため、管理者はログイン時に携帯電話での本人確認を行います。
          パスワードが漏れても、お手元の携帯電話がなければログインできなくなります。
        </p>

        {step === "email" && (
          <>
            <p style={st.note}>
              はじめに、メールアドレスの確認が必要です。
              <strong>{user.email}</strong> あてに確認メールを送ります。
            </p>
            <button style={st.primary} onClick={sendVerifyMail} disabled={busy}>
              {busy ? "送信中..." : "確認メールを送る"}
            </button>
            <button style={st.secondary} onClick={recheckEmail} disabled={busy}>
              確認しました
            </button>
            {/* 端末側が古い状態を持っていると先へ進めなくなるため、手動でも進めるようにする。
                本当に未確認なら、次の手順でFirebaseがはじくので通ってしまうことはない。 */}
            <button style={st.skip} onClick={() => { setError(""); setInfo(""); setStep("phone"); }} disabled={busy}>
              確認済みなので次へ進む
            </button>
          </>
        )}

        {step === "phone" && (
          <>
            <label style={st.label}>携帯電話の番号</label>
            <input style={st.input} type="tel" placeholder="090-1234-5678" value={phone}
              onChange={e => setPhone(e.target.value)} disabled={busy} autoFocus />
            <p style={st.hint}>この番号あてに確認コードをお送りします。番号は認証以外には使いません。</p>
            <button style={st.primary} onClick={sendCode} disabled={busy}>
              {busy ? "送信中..." : "確認コードを送る"}
            </button>
          </>
        )}

        {step === "code" && (
          <>
            <label style={st.label}>確認コード（6桁）</label>
            <input style={{ ...st.input, letterSpacing: "8px", textAlign: "center", fontSize: "24px" }}
              type="tel" inputMode="numeric" maxLength={6} placeholder="000000" value={code}
              onChange={e => setCode(e.target.value)} disabled={busy} autoFocus />
            <button style={st.primary} onClick={submitCode} disabled={busy}>
              {busy ? "確認中..." : "登録する"}
            </button>
            <button style={st.secondary} onClick={() => { setStep("phone"); setCode(""); setError(""); }} disabled={busy}>
              番号を入力し直す
            </button>
          </>
        )}

        {step === "done" && (
          <>
            <p style={st.done}>✅ 2段階認証を設定しました。</p>
            <p style={st.note}>
              次回からのログインでは、パスワードのあとに確認コードの入力が必要になります。
              携帯電話を機種変更・紛失した場合は、運営にご連絡ください。
            </p>
            <button style={st.primary} onClick={onDone}>続ける</button>
          </>
        )}

        {error && <p style={st.error}>{error}</p>}
        {info  && <p style={st.info}>{info}</p>}

        {/* 不可視のreCAPTCHA。SMSの悪用防止のためFirebaseが必須としている */}
        <div id="mfa-recaptcha" />

        {step !== "done" && canSkip && (
          <>
            <button style={st.skip} onClick={onSkip} disabled={busy}>あとで設定する</button>
            {graceText && <p style={st.hint}>{graceText}</p>}
          </>
        )}

        {step !== "done" && !canSkip && (
          <button style={st.skip} onClick={() => auth.signOut()}>ログアウト</button>
        )}
      </div>
    </div>
  );
}

const st = {
  wrap:  { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f4f8", padding: "20px" },
  card:  { backgroundColor: "#fff", padding: "36px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "100%", maxWidth: "420px", textAlign: "center" },
  icon:  { fontSize: "48px", marginBottom: "12px" },
  title: { color: "#2c3e50", margin: "0 0 12px" },
  lead:  { color: "#7f8c8d", fontSize: "13px", lineHeight: "1.9", textAlign: "left" },
  note:  { color: "#2c3e50", fontSize: "13px", lineHeight: "1.9", textAlign: "left", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "12px 14px" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginTop: "18px", marginBottom: "6px", textAlign: "left" },
  input: { width: "100%", padding: "12px", border: "1px solid #dfe6ec", borderRadius: "8px", fontSize: "16px", boxSizing: "border-box" },
  hint:  { color: "#7f8c8d", fontSize: "12px", lineHeight: "1.8", textAlign: "left", marginTop: "8px" },
  primary:   { width: "100%", marginTop: "16px", padding: "12px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  secondary: { width: "100%", marginTop: "8px", padding: "12px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  skip:  { marginTop: "18px", padding: "8px 16px", backgroundColor: "transparent", color: "#7f8c8d", border: "none", cursor: "pointer", fontSize: "13px", textDecoration: "underline" },
  error: { color: "#c0392b", fontSize: "13px", lineHeight: "1.8", textAlign: "left", backgroundColor: "#fdeaea", borderRadius: "8px", padding: "10px 12px", marginTop: "14px" },
  info:  { color: "#1e6b52", fontSize: "13px", lineHeight: "1.8", textAlign: "left", backgroundColor: "#e9f7f1", borderRadius: "8px", padding: "10px 12px", marginTop: "14px" },
  done:  { color: "#27ae60", fontSize: "16px", fontWeight: "bold", marginTop: "10px" },
};

export default MfaSetup;
